// Supported UI locales. To add one: add it here, add a JSON catalog in locales/,
// and register it in i18n.ts. `en` is the base (AU/UK spelling); other locales
// need only the keys that differ (they fall back to `en`). en-CA is a thin variant:
// Canadian English keeps British -our/-re spelling (colour, flavour, Favourites,
// centre) from the base, and only overrides the -ize family (Customize, Organize).
export type Locale = "en" | "en-US" | "en-CA" | "es";

export const LOCALES: Locale[] = ["en", "en-US", "en-CA", "es"];
export const DEFAULT_LOCALE: Locale = "en";

// Map a device/browser BCP-47 tag (e.g. "en-US", "en-GB", "es-MX") to a supported
// locale. Only US and Canadian English get their own catalog; any Spanish → es;
// EVERYTHING else → the `en` base.
//
// Deliberately NOT grouping Canada with the US: Canadian spelling is mostly
// Commonwealth (Favourites/flavour/colour/centre match our `en` base, not the US
// "Favorites/flavor"), so en-CA falls back to `en` for all of that and only differs
// on -ize words. By the same logic en-GB/en-AU/en-NZ/en-IN stay on the `en` base
// too — they're close enough that a separate catalog isn't worth it. Don't "fix"
// this later by folding en-CA into en-US; that would wrongly Americanise Canadians.
export function resolveLocale(tag: string | null | undefined): Locale {
  const t = (tag ?? "").toLowerCase();
  if (t === "en-us" || t === "en_us") return "en-US";
  if (t === "en-ca" || t === "en_ca") return "en-CA";
  if (t === "es" || t.startsWith("es-") || t.startsWith("es_")) return "es";
  return DEFAULT_LOCALE;
}

// Map the app locale to a full BCP-47 tag for Intl date/number formatting. The
// `en` base is AU/UK English (day-first dates, "Sept"); en-US stays month-first;
// es formats as Spain Spanish. en-CA intentionally uses the en-GB day-first default
// for now (Canadian date conventions are mixed: day-first, month-first, and ISO all
// occur), so only its spelling differs from the base, not its date format.
export function intlLocale(locale: Locale): string {
  switch (locale) {
    case "es":
      return "es-ES";
    case "en-US":
      return "en-US";
    default:
      return "en-GB";
  }
}
